import { useState } from 'react';
import { PlusCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePropertyPurchasePayments } from '@/hooks/usePropertyPurchasePayments';
import { PurchasePaymentForm } from './PurchasePaymentForm';
import { FixedAsset } from '@/hooks/useFixedAssets';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function PropertyPurchaseProgress({ asset }: { asset: FixedAsset }) {
  const [paymentOpen, setPaymentOpen] = useState(false);
  const { data: payments = [] } = usePropertyPurchasePayments(asset.id);

  const purchasePrice = Number(asset.purchase_price) || 0;
  const amountPaid = Number(asset.amount_paid) || 0;
  
  // Cap at 100% just for the visual bar, but show true numbers in text
  const percentPaid = purchasePrice > 0 ? Math.min(100, Math.round((amountPaid / purchasePrice) * 100)) : 0;
  const balance = Math.max(0, purchasePrice - amountPaid);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle>Purchase Progress</CardTitle>
          <Button size="sm" onClick={() => setPaymentOpen(true)} data-testid="record-purchase-payment-btn">
            <PlusCircle className="h-4 w-4 mr-2" />
            Record Payment
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm font-medium">
              <span>{formatCurrency(amountPaid, asset.currency)} paid</span>
              <span className="text-muted-foreground">{percentPaid}%</span>
              <span>{formatCurrency(purchasePrice, asset.currency)} total</span>
            </div>
            <Progress value={percentPaid} className="h-3" />
            {balance > 0 ? (
              <p className="text-sm text-muted-foreground">Balance remaining: {formatCurrency(balance, asset.currency)}</p>
            ) : (
              <p className="text-sm font-semibold text-emerald-600">Property is fully paid off!</p>
            )}
          </div>

          {payments.length > 0 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Receipt</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                      <TableCell>{p.description || '—'}</TableCell>
                      <TableCell>{p.receipt_number || '—'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(Number(p.amount), asset.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Purchase Payment</DialogTitle>
          </DialogHeader>
          <PurchasePaymentForm fixedAssetId={asset.id} assetCurrency={asset.currency} onSuccess={() => setPaymentOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
